import { sum } from '@lib/helpers'
import { type SanityFrameBuilderType } from '@frame-builder/data/sanity/queries/types'
import {
  type SanityFrameBuilderGlass,
  type SanityFrameBuilderProductGlass,
} from '@frame-builder/data/sanity/queries/types/glass'
import {
  type SanityFrameBuilderProfile,
  type SanityFrameBuilderProductProfile,
} from '@frame-builder/data/sanity/queries/types/profile'
import {
  type SanityFrameBuilderPassepartoutColor,
  type SanityFrameBuilderProductPassepartoutColor,
} from '@frame-builder/data/sanity/queries/types/passepartout'
import {
  type SanityFrameBuilderProductProfileTexture,
  type SanityFrameBuilderProfileTexture,
} from '@frame-builder/data/sanity/queries/types/profile-texture'
import {
  type SanityFrameBuilderProductServicePrices,
  type SanityFrameBuilderServicePrices,
} from '@frame-builder/data/sanity/queries/types/service-prices'
import {
  type SanityFrameBuilderProductSpacerSize,
  type SanityFrameBuilderSpacerSize,
} from '@frame-builder/data/sanity/queries/types/spacer'
import { getPriceWithDiscount } from './format'
import { getIsSizeWithinLimits } from './size'

/**
 * Calculates frame price.
 */
const getFramePrice = (
  framePerimeter: number,
  profile?: SanityFrameBuilderProfile | SanityFrameBuilderProductProfile,
  profileTexture?:
    | SanityFrameBuilderProfileTexture
    | SanityFrameBuilderProductProfileTexture,
) => {
  if (!profile || !profileTexture) {
    return 0
  }

  const profilePricePerLength = profile.price ?? 0
  const profileTexturePricePerLength = profileTexture.price ?? 0
  const framePricePerLength =
    profilePricePerLength + profileTexturePricePerLength
  const framePrice = framePricePerLength * framePerimeter

  return framePrice
}

/**
 * Calculates cut waste price.
 */
const getCutWastePrice = (
  servicePrices?:
    | SanityFrameBuilderServicePrices
    | SanityFrameBuilderProductServicePrices
    | null,
  profile?: SanityFrameBuilderProfile | SanityFrameBuilderProductProfile,
  profileTexture?:
    | SanityFrameBuilderProfileTexture
    | SanityFrameBuilderProductProfileTexture,
) => {
  if (!profile || !profileTexture) {
    return 0
  }

  const profilePricePerLength = profile.price ?? 0
  const profileTexturePricePerLength = profileTexture.price ?? 0
  const framePricePerLength =
    profilePricePerLength + profileTexturePricePerLength
  const cutWasteLength = servicePrices?.cutWaste ?? 0
  const cutWastePrice = cutWasteLength * framePricePerLength

  return cutWastePrice
}

/**
 * Calculates large frame production price.
 */
const getLargeFrameProductionPrice = (
  frameWidth: number,
  frameHeight: number,
  servicePrices?:
    | SanityFrameBuilderServicePrices
    | SanityFrameBuilderProductServicePrices
    | null,
  profile?: SanityFrameBuilderProfile | SanityFrameBuilderProductProfile,
) => {
  const isSizeWithinLimits = getIsSizeWithinLimits(
    frameWidth,
    frameHeight,
    profile?.minimumLargeFrameWidth,
    profile?.minimumLargeFrameHeight,
  )

  if (isSizeWithinLimits) {
    return 0
  }

  const largeFrameProductionPrice = servicePrices?.largeFrameProductionFee ?? 0

  return largeFrameProductionPrice
}

/**
 * Calculates mount price.
 */
const getPassepartoutPrice = (
  frameBuilderType: SanityFrameBuilderType,
  frameArea: number,
  servicePrices?:
    | SanityFrameBuilderServicePrices
    | SanityFrameBuilderProductServicePrices
    | null,
  passepartoutColor?:
    | SanityFrameBuilderPassepartoutColor
    | SanityFrameBuilderProductPassepartoutColor,
) => {
  if (!passepartoutColor) {
    return 0
  }

  const pricePerArea = passepartoutColor.price ?? 0

  let productionPrice = 0
  let price = pricePerArea * frameArea

  // Limit minimum price
  if (typeof passepartoutColor.minimumPrice !== 'undefined') {
    price = Math.max(price, passepartoutColor.minimumPrice)
  }

  // Add production fee for custom mount products
  if (
    frameBuilderType === 'passepartout' &&
    typeof servicePrices?.customPassepartoutProductionFee !== 'undefined'
  ) {
    productionPrice = servicePrices.customPassepartoutProductionFee
  }

  // Add price multiplier for custom mount products
  if (
    frameBuilderType === 'passepartout' &&
    typeof servicePrices?.customPassepartoutPriceMultiplier !== 'undefined'
  ) {
    price = price * servicePrices.customPassepartoutPriceMultiplier
  }

  const totalPrice = [productionPrice, price].reduce(sum, 0)

  return totalPrice
}

/**
 * Calculates glass price.
 */
const getGlassPrice = (
  frameArea: number,
  glass?: SanityFrameBuilderGlass | SanityFrameBuilderProductGlass,
) => {
  if (!glass?.price) {
    return 0
  }

  const glassPricePerArea = glass.price ?? 0
  const glassPrice = glassPricePerArea * frameArea

  return glassPrice
}

/**
 * Calculates spacer price.
 */
const getSpacerPrice = (
  framePerimeter: number,
  spacerSize?:
    | SanityFrameBuilderSpacerSize
    | SanityFrameBuilderProductSpacerSize,
) => {
  if (!spacerSize) {
    return 0
  }

  const spacerFixedPrice = spacerSize.fixedPrice ?? 0
  const spacerPricePerLength = spacerSize.price ?? 0
  const spacerPrice = spacerFixedPrice + spacerPricePerLength * framePerimeter

  return spacerPrice
}

/**
 * Calculates backboard price.
 */
export const getBackboardPrice = (
  frameArea: number,
  servicePrices?:
    | SanityFrameBuilderServicePrices
    | SanityFrameBuilderProductServicePrices
    | null,
) => {
  const backboardPrice = servicePrices?.backBoard ?? 0
  const backBoardMinimumPrice = servicePrices?.backBoardMinimum ?? 0

  return Math.max(backboardPrice * frameArea, backBoardMinimumPrice)
}

/**
 * Calculates frame builder price.
 */
export const getFrameBuilderPrice = (
  frameBuilderType: SanityFrameBuilderType,
  servicePrices:
    | SanityFrameBuilderServicePrices
    | SanityFrameBuilderProductServicePrices
    | null,
  frameWidth: number,
  frameHeight: number,
  frameArea: number,
  framePerimeter: number,
  profile?: SanityFrameBuilderProfile | SanityFrameBuilderProductProfile,
  profileTexture?:
    | SanityFrameBuilderProfileTexture
    | SanityFrameBuilderProductProfileTexture,
  passepartoutColor?:
    | SanityFrameBuilderPassepartoutColor
    | SanityFrameBuilderProductPassepartoutColor,
  glass?: SanityFrameBuilderGlass | SanityFrameBuilderProductGlass,
  spacerSize?:
    | SanityFrameBuilderSpacerSize
    | SanityFrameBuilderProductSpacerSize,
) => {
  // Mount price
  const passepartoutPrice = getPassepartoutPrice(
    frameBuilderType,
    frameArea,
    servicePrices,
    passepartoutColor,
  )

  if (frameBuilderType === 'passepartout') {
    return passepartoutPrice
  }

  // Production price
  const productionPrice = servicePrices?.productionFee ?? 0

  // Frame price
  const framePrice = getFramePrice(framePerimeter, profile, profileTexture)

  // Cut waste price
  const cutWastePrice = getCutWastePrice(servicePrices, profile, profileTexture)

  // Large frame production price
  const largeFrameProductionPrice = getLargeFrameProductionPrice(
    frameWidth,
    frameHeight,
    servicePrices,
    profile,
  )

  // Floating frame
  if (profile?.floatingFrame) {
    const floatingFramePrice = [
      productionPrice,
      framePrice,
      cutWastePrice,
      largeFrameProductionPrice,
    ].reduce(sum, 0)

    return floatingFramePrice
  }

  // Backboard price
  const backboardPrice = getBackboardPrice(frameArea, servicePrices)

  // Glass price
  const glassPrice = getGlassPrice(frameArea, glass)

  // Spacer price
  const spacerPrice = getSpacerPrice(framePerimeter, spacerSize)

  const totalPrice = [
    productionPrice,
    framePrice,
    cutWastePrice,
    largeFrameProductionPrice,
    backboardPrice,
    passepartoutPrice,
    glassPrice,
    spacerPrice,
  ].reduce(sum, 0)

  return totalPrice
}

/**
 * Calculates frame builder price with discounts.
 */
export const getFrameBuilderPriceWithDiscount = (
  frameBuilderType: SanityFrameBuilderType,
  servicePrices:
    | SanityFrameBuilderServicePrices
    | SanityFrameBuilderProductServicePrices
    | null,
  frameWidth: number,
  frameHeight: number,
  frameArea: number,
  framePerimeter: number,
  profile?: SanityFrameBuilderProfile | SanityFrameBuilderProductProfile,
  profileTexture?:
    | SanityFrameBuilderProfileTexture
    | SanityFrameBuilderProductProfileTexture,
  passepartoutColor?:
    | SanityFrameBuilderPassepartoutColor
    | SanityFrameBuilderProductPassepartoutColor,
  glass?: SanityFrameBuilderGlass | SanityFrameBuilderProductGlass,
  spacerSize?:
    | SanityFrameBuilderSpacerSize
    | SanityFrameBuilderProductSpacerSize,
) => {
  // Mount price
  const passepartoutPrice = getPassepartoutPrice(
    frameBuilderType,
    frameArea,
    servicePrices,
    passepartoutColor,
  )
  const passepartoutPriceWithDiscount = getPriceWithDiscount(
    passepartoutPrice,
    passepartoutColor?.discountPercent,
  )

  if (frameBuilderType === 'passepartout') {
    return passepartoutPriceWithDiscount
  }

  // Production price
  const productionPrice = servicePrices?.productionFee ?? 0

  // Frame price
  const framePrice = getFramePrice(framePerimeter, profile, profileTexture)
  const framePriceWithDiscount = getPriceWithDiscount(
    framePrice,
    profileTexture?.discountPercent,
  )

  // Cut waste price
  const cutWastePrice = getCutWastePrice(servicePrices, profile, profileTexture)
  const cutWastePriceWithDiscount = getPriceWithDiscount(
    cutWastePrice,
    profileTexture?.discountPercent,
  )

  // Large frame production price
  const largeFrameProductionPrice = getLargeFrameProductionPrice(
    frameWidth,
    frameHeight,
    servicePrices,
    profile,
  )

  // Floating frame
  if (profile?.floatingFrame) {
    const floatingFramePrice = [
      productionPrice,
      framePriceWithDiscount,
      cutWastePriceWithDiscount,
      largeFrameProductionPrice,
    ].reduce(sum, 0)

    return floatingFramePrice
  }

  // Backboard price
  const backboardPrice = getBackboardPrice(frameArea, servicePrices)

  // Glass price
  const glassPrice = getGlassPrice(frameArea, glass)
  const glassPriceWithDiscount = getPriceWithDiscount(
    glassPrice,
    glass?.discountPercent,
  )

  // Spacer price
  const spacerPrice = getSpacerPrice(framePerimeter, spacerSize)

  const totalPrice = [
    productionPrice,
    framePriceWithDiscount,
    cutWastePriceWithDiscount,
    largeFrameProductionPrice,
    backboardPrice,
    passepartoutPriceWithDiscount,
    glassPriceWithDiscount,
    spacerPrice,
  ].reduce(sum, 0)

  return totalPrice
}
